<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { OnClickOutside } from '@vueuse/components';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

// 表单和对话框组件
import KnowledgeBaseForm from 'dashboard/components-next/pilot/pageComponents/knowledgeBase/KnowledgeBaseForm.vue';
import CreateKnowledgeBaseDocumentDialog from 'dashboard/components-next/pilot/pageComponents/knowledgeBaseDocument/CreateKnowledgeBaseDocumentDialog.vue';

// 侧边栏组件
import KnowledgeBaseDetailSidebar from 'dashboard/components-next/pilot/pageComponents/knowledgeBase/KnowledgeBaseDetailSidebar.vue';

// Tab 内容组件
import DocumentList from 'dashboard/components-next/pilot/pageComponents/knowledgeBase/DocumentList.vue';
import FaqList from 'dashboard/components-next/pilot/pageComponents/knowledgeBase/FaqList.vue';
import RecallTest from 'dashboard/components-next/pilot/pageComponents/knowledgeBase/RecallTest.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const knowledgeBaseId = computed(() => Number(route.params.id));

// ============================================================
// 知识库信息
// ============================================================
const uiFlags = useMapGetter('pilotKnowledgeBases/getUIFlags');
const isFetchingStore = computed(() => uiFlags.value.fetchingList);

const knowledgeBase = ref(null);
const isLoading = ref(true);

// 弹窗状态
const showAssistantsPopover = ref(false);
const isEditPopoverOpen = ref(false);
const assistantsButtonRef = ref(null);
const editButtonRef = ref(null);
const knowledgeBaseFormRef = ref(null);
const uploadDocumentDialog = ref(null);

// 侧边栏状态
const sidebarCollapsed = ref(false);

// ============================================================
// Tab 切换状态（由侧边栏驱动）
// ============================================================
const currentTabName = computed(() => {
  if (route.name === 'pilot_knowledge_bases_faqs') return 'faqs';
  if (route.name === 'pilot_knowledge_bases_recall_test') return 'recall_test';
  return 'documents';
});

// ============================================================
// Header 添加按钮
// ============================================================
const isHeaderAddMenuOpen = ref(false);

const documentAddMenuItems = [
  { label: '上传文档', icon: 'i-ri-file-word-2-line', action: 'uploadDocument' },
  { label: '抓取网页', icon: 'i-lucide-globe', action: 'crawlWebpage' },
  {
    label: '抓取公众号',
    icon: 'i-ri-wechat-2-line',
    action: 'crawlWechat',
    developmentToast: true,
  },
  {
    label: '同步营销云',
    icon: 'i-lucide-cloud-check',
    action: 'syncMarketingCloud',
    developmentToast: true,
  },
];

const faqAddMenuItems = [
  { label: '添加问答', icon: 'i-lucide-message-circle-question', action: 'addFaq' },
  { label: '导入问答', icon: 'i-lucide-file-spreadsheet', action: 'importFaq' },
];

const headerAddMenuItems = computed(() => {
  return currentTabName.value === 'faqs' ? faqAddMenuItems : documentAddMenuItems;
});

const headerAddButtonLabel = computed(() => {
  return currentTabName.value === 'faqs'
    ? t('PILOT.KNOWLEDGE_BASES.FAQS.ADD_BUTTON')
    : t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.ADD_BUTTON');
});

const handleHeaderAddAction = item => {
  isHeaderAddMenuOpen.value = false;
  if (item.developmentToast) {
    useAlert(t('PILOT.KNOWLEDGE_BASES.SIDEBAR.COMING_SOON'));
    return;
  }
  handleSidebarSelect({ action: item.action });
};

// ============================================================
// 获取知识库信息
// ============================================================
const fetchKnowledgeBase = async () => {
  try {
    const response = await store.dispatch('pilotKnowledgeBases/show', {
      id: knowledgeBaseId.value,
    });
    knowledgeBase.value = response.payload;
    isLoading.value = false;
  } catch (error) {
    console.error('Failed to fetch knowledge base:', error);
    isLoading.value = false;
  }
};

// ============================================================
// 侧边栏 Tab 切换处理
// ============================================================
const handleTabChange = tabId => {
  const routeNames = {
    documents: 'pilot_knowledge_bases_documents',
    faqs: 'pilot_knowledge_bases_faqs',
    recall_test: 'pilot_knowledge_bases_recall_test',
  };
  const routeName = routeNames[tabId];
  if (route.name !== routeName) {
    router.push({
      name: routeName,
      params: { id: knowledgeBaseId.value },
    });
  }
};

// ============================================================
// Header 操作
// ============================================================
const goBack = () => {
  router.push({ name: 'pilot_knowledge_bases_index' });
};

const goToEdit = () => {
  isEditPopoverOpen.value = true;
  nextTick(() => {
    if (knowledgeBaseFormRef.value && knowledgeBase.value) {
      knowledgeBaseFormRef.value.setFormData(knowledgeBase.value);
    }
  });
};

const closeEditPopover = () => {
  isEditPopoverOpen.value = false;
};

const updateKnowledgeBase = async knowledgeBaseDetails => {
  try {
    const response = await store.dispatch('pilotKnowledgeBases/update', {
      id: knowledgeBase.value.id,
      data: knowledgeBaseDetails,
    });

    if (response?.payload || response?.data?.payload) {
      const updatedKB = response.payload || response.data.payload;
      knowledgeBase.value = {
        ...knowledgeBase.value,
        name: updatedKB.name,
        description: updatedKB.description,
        status: updatedKB.status,
        logo_url: updatedKB.logo_url,
      };
    }

    useAlert(t('PILOT.KNOWLEDGE_BASES.EDIT.SUCCESS_MESSAGE'));
    closeEditPopover();
  } catch (error) {
    const errorMessage =
      error?.message || t('PILOT.KNOWLEDGE_BASES.EDIT.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const getEditPopoverStyle = () => {
  if (!editButtonRef.value) return {};
  const buttonRect = editButtonRef.value.getBoundingClientRect();
  return {
    top: `${buttonRect.bottom + 8}px`,
    left: `${buttonRect.left}px`,
    minWidth: '400px',
  };
};

// ============================================================
// 助手列表弹窗
// ============================================================
const linkedAssistants = computed(() => {
  return knowledgeBase.value?.assistants || [];
});

const getAssistantsPopoverStyle = () => {
  if (!assistantsButtonRef.value) return {};
  const buttonRect = assistantsButtonRef.value.getBoundingClientRect();
  return {
    top: `${buttonRect.bottom + 8}px`,
    left: `${buttonRect.left}px`,
    minWidth: '27.5rem',
  };
};

const handleAssistantClick = assistant => {
  router.push({
    name: 'pilot_assistant_config',
    params: {
      accountId: route.params.accountId,
      assistantId: assistant.id,
    },
  });
  showAssistantsPopover.value = false;
};

// ============================================================
// 侧边栏操作
// ============================================================
const handleSidebarSelect = item => {
  switch (item.action) {
    case 'uploadDocument':
      handleUploadDocument();
      break;
    case 'crawlWebpage':
      handleCrawlWebpage();
      break;
    case 'crawlWechat':
      handleCrawlWechat();
      break;
    case 'syncMarketingCloud':
      handleSyncMarketingCloud();
      break;
    case 'addFaq':
      handleCreateFaq();
      break;
    case 'importFaq':
      handleImportFaq();
      break;
    default:
      break;
  }
};

const handleSidebarToggle = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value;
};

// ============================================================
// 上传文档
// ============================================================
const handleUploadDocument = () => {
  router.push({
    name: 'pilot_documents_file_upload',
    params: { id: knowledgeBaseId.value },
  });
};

// ============================================================
// 抓取网页
// ============================================================
const handleCrawlWebpage = () => {
  router.push({
    name: 'pilot_documents_web_crawl',
    params: { id: knowledgeBaseId.value },
  });
};

// ============================================================
// 抓取公众号
// ============================================================
const handleCrawlWechat = () => {
  router.push({
    name: 'pilot_documents_wechat_crawl',
    params: { id: knowledgeBaseId.value },
  });
};

// ============================================================
// 同步营销云
// ============================================================
const handleSyncMarketingCloud = () => {
  router.push({
    name: 'pilot_documents_mip_sync',
    params: { id: knowledgeBaseId.value },
  });
};

// ============================================================
// 创建 FAQ
// ============================================================
const handleCreateFaq = () => {
  router.push({
    name: 'pilot_faqs_add',
    params: { knowledgeBaseId: knowledgeBaseId.value },
  });
};

// ============================================================
// 导入 FAQ
// ============================================================
const handleImportFaq = () => {
  router.push({
    name: 'pilot_faqs_upload',
    params: { id: knowledgeBaseId.value },
  });
};

const onUploadSuccess = async () => {
  await fetchKnowledgeBase();
};

// ============================================================
// 刷新回调（由子组件触发）
// ============================================================
const handleRefresh = async () => {
  await fetchKnowledgeBase();
};

onMounted(() => {
  fetchKnowledgeBase();
});
</script>

<template>
  <section class="flex w-full h-full gap-4 p-4 overflow-hidden bg-n-surface-1">
    <!-- 桌面端侧边栏 -->
    <div class="hidden md:flex flex-shrink-0 h-full">
      <KnowledgeBaseDetailSidebar
        :knowledge-base="knowledgeBase"
        :current-tab="currentTabName"
        :collapsed="sidebarCollapsed"
        class="h-full rounded-xl overflow-hidden"
        @tab-change="handleTabChange"
        @select="handleSidebarSelect"
        @toggle-collapse="handleSidebarToggle"
      />
    </div>

    <!-- 主内容区域 -->
    <main
      class="flex-1 flex flex-col min-w-0 h-full overflow-hidden bg-n-slate-white dark:bg-n-slate-800 rounded-xl"
    >
      <!-- Loading -->
      <div
        v-if="isLoading"
        class="flex items-center justify-center flex-1 w-full"
      >
        <Spinner />
      </div>

      <div v-else-if="knowledgeBase" class="flex flex-col h-full w-full">
        <!-- Header -->
        <header
          class="sticky top-0 z-10 px-6 bg-n-slate-white dark:bg-n-slate-800 rounded-t-xl"
        >
          <div class="w-full">
            <!-- 第一行：左侧信息 + 右侧操作 -->
            <div class="flex items-center justify-between h-12">
              <!-- 左侧：返回、名称、编辑、状态、助手 -->
              <div class="flex items-center gap-3">
                <!-- 返回按钮 -->
                <button
                  type="button"
                  class="p-2 -ml-2 text-n-slate-600 dark:text-n-slate-400 hover:bg-n-slate-100 dark:hover:bg-n-slate-700 rounded-lg transition-colors"
                  @click="goBack"
                >
                  <Icon icon="i-lucide-arrow-left" class="h-5 w-5" />
                </button>

                <!-- 名称 -->
                <h1
                  class="text-lg font-semibold text-n-slate-900 dark:text-n-slate-100"
                >
                  {{ knowledgeBase.name }}
                </h1>

                <!-- 编辑按钮 -->
                <div ref="editButtonRef" class="relative">
                  <Button
                    icon="i-lucide-pencil-line"
                    size="sm"
                    ghost
                    @click="goToEdit"
                  />
                </div>

                <!-- 状态标签 -->
                <div
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                  :class="
                    knowledgeBase.status === 'published'
                      ? 'bg-n-blue-1 dark:bg-n-blue-2 text-n-blue-10 dark:text-n-blue-9'
                      : 'bg-n-amber-1 dark:bg-n-amber-2 text-n-amber-10 dark:text-n-amber-9'
                  "
                >
                  <span
                    class="w-1.5 h-1.5 rounded-full"
                    :class="
                      knowledgeBase.status === 'published'
                        ? 'bg-n-blue-5'
                        : 'bg-n-amber-5'
                    "
                  />
                  <span>{{
                    knowledgeBase.status === 'published'
                      ? t('PILOT.KNOWLEDGE_BASES.STATUS.PUBLISHED')
                      : t('PILOT.KNOWLEDGE_BASES.STATUS.DRAFT')
                  }}</span>
                </div>

                <!-- 助手按钮 -->
                <div ref="assistantsButtonRef" class="relative">
                  <Button
                    icon="i-woot-captain"
                    size="sm"
                    ghost
                    @click="showAssistantsPopover = !showAssistantsPopover"
                  />
                </div>
              </div>

              <!-- 右侧:更新时间 + 添加按钮 -->
              <div class="flex items-center gap-3">
                <span class="text-sm text-n-slate-10 dark:text-n-slate-10">
                  {{ t('PILOT.KNOWLEDGE_BASES.UPDATED') }}
                  {{ dynamicTime(knowledgeBase.updated_at) }}
                </span>
                <!-- 添加按钮 -->
                <div v-if="currentTabName !== 'recall_test'" class="relative">
                  <Button
                    :label="headerAddButtonLabel"
                    icon="i-lucide-plus"
                    size="sm"
                    :class="isHeaderAddMenuOpen ? 'bg-n-alpha-2' : ''"
                    @click="isHeaderAddMenuOpen = !isHeaderAddMenuOpen"
                  />
                  <div
                    v-if="isHeaderAddMenuOpen"
                    v-on-clickaway="() => isHeaderAddMenuOpen = false"
                    class="absolute top-full mt-1 right-0 flex flex-col bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak w-48 rounded-xl py-1.5 z-20"
                  >
                    <button
                      v-for="item in headerAddMenuItems"
                      :key="item.action"
                      class="flex items-center gap-2.5 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors cursor-pointer"
                      @click="handleHeaderAddAction(item)"
                    >
                      <Icon :icon="item.icon" class="size-4 text-n-slate-11" />
                      {{ item.label }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        <!-- 主内容区域 -->
        <!-- 文档/FAQ Tab：可滚动 -->
        <div
          v-if="currentTabName !== 'recall_test'"
          class="flex-1 w-full px-6 overflow-y-auto"
        >
          <div class="w-full py-6">
            <!-- 文档 Tab -->
            <DocumentList
              v-if="currentTabName === 'documents' && knowledgeBase"
              :knowledge-base="knowledgeBase"
              :initial-documents="knowledgeBase.documents || []"
              :initial-meta="{
                total_count: knowledgeBase.documents_count || 0,
                current_page: 1,
                total_pages: 1,
                per_page: 25,
              }"
              :upload-dialog-ref="uploadDocumentDialog"
              @refresh="handleRefresh"
              @upload="handleUploadDocument"
            />

            <!-- FAQ Tab -->
            <FaqList
              v-if="currentTabName === 'faqs' && knowledgeBase"
              :knowledge-base="knowledgeBase"
              @refresh="handleRefresh"
              @create="handleCreateFaq"
            />
          </div>
        </div>

        <!-- Recall Test Tab：固定高度，内部自行滚动 -->
        <div
          v-else
          class="flex-1 w-full px-6 py-6 overflow-hidden"
        >
          <RecallTest
            v-if="knowledgeBase"
            :knowledge-base="knowledgeBase"
          />
        </div>
      </div>
    </main>
  </section>

  <!-- 弹窗组件 -->

  <!-- 上传文档对话框 -->
  <CreateKnowledgeBaseDocumentDialog
    ref="uploadDocumentDialog"
    :knowledge-base-id="knowledgeBaseId"
    @success="onUploadSuccess"
  />

  <!-- 知识库编辑弹窗 -->
  <Teleport to="body">
    <transition
      name="fade"
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isEditPopoverOpen && knowledgeBase"
        class="fixed z-50"
        :style="getEditPopoverStyle()"
      >
        <OnClickOutside @trigger="closeEditPopover">
          <div
            class="bg-n-alpha-3 backdrop-blur-[100px] border border-n-container outline outline-1 outline-n-container rounded-xl shadow-xl overflow-hidden"
          >
            <div class="p-6">
              <KnowledgeBaseForm
                ref="knowledgeBaseFormRef"
                :is-edit="true"
                @submit="updateKnowledgeBase"
                @cancel="closeEditPopover"
              />
            </div>
          </div>
        </OnClickOutside>
      </div>
    </transition>
  </Teleport>

  <!-- 助手列表弹窗 -->
  <Teleport to="body">
    <transition
      name="fade"
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showAssistantsPopover && knowledgeBase"
        class="fixed z-50"
        :style="getAssistantsPopoverStyle()"
      >
        <OnClickOutside @trigger="showAssistantsPopover = false">
          <div
            class="pt-5 pb-3 bg-n-alpha-3 backdrop-blur-[100px] outline outline-n-container outline-1 z-50 rounded-xl shadow-md flex flex-col gap-4"
          >
            <div
              class="flex items-center justify-between gap-4 px-6 pb-3 border-b border-n-alpha-2"
            >
              <div class="flex flex-col gap-1">
                <div class="flex items-center gap-2">
                  <h2
                    class="text-base font-medium cursor-pointer text-n-slate-12 w-fit hover:underline"
                  >
                    {{ t('PILOT.ASSISTANT_SWITCHER.ASSISTANTS') }}
                  </h2>
                </div>
                <p class="text-sm text-n-slate-11">
                  {{
                    t('PILOT.KNOWLEDGE_BASES.ASSISTANTS_COUNT.MULTIPLE', {
                      count: linkedAssistants.length,
                    })
                  }}
                </p>
              </div>
            </div>
            <div
              v-if="linkedAssistants.length > 0"
              class="flex flex-col gap-2 px-4"
            >
              <Button
                v-for="assistant in linkedAssistants"
                :key="assistant.id"
                :label="assistant.name"
                variant="ghost"
                color="slate"
                trailing-icon
                class="!justify-end !px-2 !py-2 hover:!bg-n-alpha-2 h-9"
                size="sm"
                @click="handleAssistantClick(assistant)"
              >
                <span class="text-sm font-medium truncate text-n-slate-12">
                  {{ assistant.name || '' }}
                </span>
                <Avatar
                  :name="assistant.name"
                  :size="20"
                  icon-name="i-lucide-bot"
                  rounded-full
                />
              </Button>
            </div>
            <div v-else class="flex flex-col items-center gap-2 px-4 py-3">
              <p class="text-sm text-n-slate-11">
                {{ t('PILOT.ASSISTANT_SWITCHER.EMPTY_LIST') }}
              </p>
            </div>
          </div>
        </OnClickOutside>
      </div>
    </transition>
  </Teleport>
</template>
